import { Component, ChangeDetectionStrategy, HostBinding, ViewEncapsulation, Input, OnInit, Output, EventEmitter, ViewChild, ElementRef,
    OnChanges, SimpleChanges, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { consumeEvent } from '@eui/core';

import { BaseDirective } from '../../base.directive';
import { EuiMenuItem } from './models/eui-menu-item.model';

@Component({
    selector: 'eui-menu',
    templateUrl: './eui-menu.component.html',
    styleUrls: ['./styles/_index.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.None,
})
export class EuiMenuComponent extends BaseDirective implements OnInit, OnChanges {

    @Input() items: EuiMenuItem[];
    @Input() isCollapsed = false;
    @Input() hasFilter = false;
    @Input() hasIcons = false;
    @Input() hasTooltip = false;
    @Input() expandAllItems = false;
    @Input() fragmentId: string;

    @Input() searchFilterLabel: string;
    @Input() expandMenuLabel: string;
    @Input() collapseMenuLabel: string;
    @Input() externalLinkLabel: string;

    @Output() onClicked: EventEmitter<any> = new EventEmitter();
    @Output() onItemClicked: EventEmitter<EuiMenuItem> = new EventEmitter();

    @ViewChild('input', { static: false }) inputFilterElement: ElementRef<any>;

    itemsState: any[];

    constructor(
        private router: Router,
        private route: ActivatedRoute,
    ) {
        super();
    }

    @HostBinding('class')
    get cssClasses() {
        return [
            super.getCssClasses('eui-menu-wrapper'),
            this.isCollapsed ? 'eui-menu--collapsed' : '',
            !this.hasIcons ? 'eui-menu--no-icons' : '',
        ].join(' ').trim();
    }

    @HostListener('click', ['$event'])
    stopPropagation(event: MouseEvent) {
        event.stopPropagation();
    }

    ngOnInit() {
        // Labels default values - TODO : translations
        if (!this.searchFilterLabel) { this.searchFilterLabel = 'Search filter'; }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.items) {
            this.items = this.items.map((item) => {

                // remapping to EuiMenuItem class
                const mappedItem = Object.assign(item, { visible: item.visible === undefined ? true : item.visible, filtered: true });
                if (mappedItem.children) {
                    mappedItem.children.map((itemSub) => {
                        if (itemSub.children) {
                            itemSub.children.map((itemSubSub) => {
                                return Object.assign(itemSubSub, {
                                    visible: itemSubSub.visible === undefined ? true : itemSubSub.visible, filtered: true
                                });
                            });
                        }
                        return Object.assign(itemSub, { visible: itemSub.visible === undefined ? true : itemSub.visible, filtered: true });
                    });
                }

                // expand all parents with children
                if (this.expandAllItems && mappedItem.children) {
                    mappedItem.expanded = true;
                }
                return mappedItem;
            });

            const currentUrl = window.location.href + '/';

            this.items.forEach((item) => {
                if (item.children) {
                    item.children.forEach((itemSub) => {
                        if (itemSub.url && currentUrl.indexOf(itemSub.url.substr(1)) > -1) {
                            setTimeout(() => this.onExpandToggle(item));
                            return;
                        } else {
                            if (itemSub.children) {
                                itemSub.children.forEach((itemSubSub) => {
                                    if (itemSubSub.url && currentUrl.indexOf(itemSubSub.url.substr(1)) > -1) {
                                        setTimeout(() => this.onExpandToggle(itemSub));
                                        setTimeout(() => this.onExpandToggle(item));
                                        return;
                                    }
                                });
                            }
                        }
                    });
                }
            });
        }

        // Expand / Collapse All items
        if (changes.expandAllItems) {
            this.expandAllItems = changes.expandAllItems.currentValue;
            if (this.expandAllItems) {
                this._onToggleExpandAllItems(true);
            } else {
                this._onToggleExpandAllItems(false);
            }
        }
    }

    public onClick(item: EuiMenuItem) {
        if (item.urlExternal) {
            window.open(item.urlExternal, '_blank');
        } else if (item.url) {
            this.router.navigate([item.url], { relativeTo: this.route, fragment: this.fragmentId });
        } else {
            if (typeof (item.command) === 'function') {
                item.command();
            } else {
                this.onExpandToggle(item);
            }
        }

        if (item.link) {
            this.items = this.items.map((it) => {
                it.active = false;
                if (it.id === item.id) {
                    it.active = true;
                }
                return it;
            });
        }

        this.onClicked.emit(true);
        this.onItemClicked.emit(item);
    }

    public onExpandToggle(item: EuiMenuItem) {
        const itemIdx = this.items.indexOf(item);
        if (itemIdx > -1) {
            this.items[itemIdx].expanded = !this.items[itemIdx].expanded;
        } else {
            this.items.forEach((i) => {
                if (i.children) {
                    const itemIdx2 = i.children.indexOf(item);
                    if (itemIdx2 > -1) {
                        i.children[itemIdx2].expanded = !i.children[itemIdx2].expanded;
                    }
                }
            });
        }
    }

    public onMenuFilterClick(event: Event) {
        consumeEvent(event);
    }

    public onFilter(event: any) {
        this.items = this.items.map((item) => {

            if (item.children || item.label.toLowerCase().indexOf(event.toLowerCase()) !== -1) {

                if (item.children) {
                    const children = item.children.map((subItem) => {
                        if (subItem.children || subItem.label.toLowerCase().indexOf(event.toLowerCase()) !== -1) {

                            if (subItem.children) {
                                const children2 = subItem.children.map((subItem2) => {
                                    if (subItem2.children || subItem2.label.toLowerCase().indexOf(event.toLowerCase()) !== -1) {
                                        return Object.assign(subItem2, { filtered: true });
                                    } else {
                                        return Object.assign(subItem2, { filtered: false });
                                    }
                                });

                                Object.assign(subItem, { children: children2 });
                                return Object.assign(subItem, {
                                    filtered: subItem.children.filter((l) => l['filtered'] === true).length > 0,
                                });
                            }

                            return Object.assign(subItem, { filtered: true });
                        } else {
                            return Object.assign(subItem, { filtered: false });
                        }
                    });

                    Object.assign(item, { children });
                    return Object.assign(item, { filtered: item.children.filter((l) => l['filtered'] === true).length > 0 });
                } else {
                    return Object.assign(item, { filtered: true });
                }
            } else {
                return Object.assign(item, { filtered: false });
            }
        });
    }

    public onClearInput() {
        this.inputFilterElement.nativeElement.value = '';
        this.onFilter('');
        this.inputFilterElement.nativeElement.focus();
    }

    private _onToggleExpandAllItems(state: boolean) {
        if (this.items) {
            this.items = this.items.map((item) => {
                if (item.children) {
                    item.expanded = state;
                }
                return Object.assign(item, { visible: item.visible === undefined ? true : item.visible });
            });
        }
    }
}
